import { ContentCopy, DashboardCustomize, Delete, Edit, Pageview, RestoreFromTrash } from '@mui/icons-material';
import { Box, Button, Checkbox, Divider, FormControlLabel, Grid, IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { DataGrid } from 'src/shared/components/organisms/dataGrid';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { AssociationAndDistrictSelect } from 'src/shared/components/organisms/associationAndDistrictSelect';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import {
  getAllGroupMessageEmailTemplates,
  getDeletedGroupMessageEmailTemplates,
  updateGroupMessageEmailTemplate,
  deleteGroupMessageEmailTemplate,
  restoreGroupMessageEmailTemplate,
} from 'src/shared/services/groupMessageService';
import { useGlobals } from 'src/shared/contexts/appContext';
import { District } from 'src/shared/models/district/district';
import { Association } from 'src/shared/models/association/association';
import { isDesignModeCompatible } from '../builder/parse';
import { EmailBuilderModal, EmailBuilderMode } from '../components/emailBuilderModal';
import { GroupMessageEmailTemplateView } from '../components/groupMessageEmailTemplateView';
import { GroupMessageEmailTemplate } from '../models/groupMessageEmailTemplate';
import { EmailTemplateDistributeModal } from '../components/emailTemplateDistributeModal';

type TemplateForm = {
  name: string;
  body: string;
};

export const GroupMessageEmailTemplatesPage = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();

  const [activeTab, setActiveTab] = useState(0);
  const [templates, setTemplates] = useState<GroupMessageEmailTemplate[]>([]);
  const [deletedTemplates, setDeletedTemplates] = useState<GroupMessageEmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editTemplate, setEditTemplate] = useState<GroupMessageEmailTemplate | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTemplate, setDeleteTemplate] = useState<GroupMessageEmailTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [distributeTemplate, setDistributeTemplate] = useState<GroupMessageEmailTemplate | null>(null);
  const [showOnlyFederation, setShowOnlyFederation] = useState(false);
  const [district, setDistrict] = useState<District | undefined>();
  const [association, setAssociation] = useState<Association | undefined>();

  const canDistribute = (currentUser?.associations?.length ?? 0) > 1;

  const applyFilter = (list: GroupMessageEmailTemplate[]) =>
    list.filter((tmpl) => {
      if (showOnlyFederation) return tmpl.localAss_id === 0 && tmpl.countyAss_id === 0;
      if (association) return tmpl.localAss_id === association.localAss_id;
      if (district) return tmpl.countyAss_id === district.countyAss_id;
      return true;
    });

  const visibleTemplates = applyFilter(templates);
  const visibleDeletedTemplates = applyFilter(deletedTemplates);

  const schema = yup.object({
    name: yup
      .string()
      .min(3, t('common:validationMessages.minLength', { count: 3 }))
      .max(30, t('common:validationMessages.maxLength', { count: 30 }))
      .required(t('common:validationMessages.required')),
    body: yup.string().required(t('common:validationMessages.required')),
  });

  const formMethods = useForm<TemplateForm>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  });

  const { handleSubmit, reset, watch } = formMethods;

  // Tabs i edit-modalen: redigera / förhandsgranska, samt designläge-knapp.
  const [editPreviewTab, setEditPreviewTab] = useState<'preview' | 'edit'>('edit');
  const [builderMode, setBuilderMode] = useState<EmailBuilderMode | null>(null);
  const editBody = watch('body');
  const designModeAvailable = isDesignModeCompatible(editBody);
  const effectivePreviewTab = designModeAvailable ? 'preview' : editPreviewTab;

  const fetchActive = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getAllGroupMessageEmailTemplates();
      if (isMounted()) setTemplates(res.data.filter((tmpl) => tmpl.id > 0));
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setIsLoading(false);
    }
  }, [isMounted]);

  const fetchDeleted = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getDeletedGroupMessageEmailTemplates();
      if (isMounted()) setDeletedTemplates(res.data);
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setIsLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    if (activeTab === 0) fetchActive();
    else fetchDeleted();
  }, [activeTab, fetchActive, fetchDeleted]);

  const openEdit = (template: GroupMessageEmailTemplate) => {
    setEditTemplate(template);
    reset({ name: template.name, body: template.body });
  };

  const onSave = async (data: TemplateForm) => {
    if (!editTemplate) return;
    setIsSaving(true);
    try {
      await updateGroupMessageEmailTemplate(editTemplate.id, data);
      if (isMounted()) {
        toast.success(<>{t('groupMessage:emailTemplates.saveSuccess')}</>);
        setEditTemplate(null);
        fetchActive();
      }
    } catch (e) {
      if (isMounted()) toast.error(<>{t('groupMessage:emailTemplates.saveError')}</>);
    } finally {
      if (isMounted()) setIsSaving(false);
    }
  };

  const onDelete = async () => {
    if (!deleteTemplate) return;
    setIsDeleting(true);
    try {
      await deleteGroupMessageEmailTemplate(deleteTemplate.id);
      if (isMounted()) {
        toast.success(<>{t('groupMessage:emailTemplates.deleteSuccess')}</>);
        setDeleteTemplate(null);
        fetchActive();
      }
    } catch (e) {
      if (isMounted()) toast.error(<>{t('groupMessage:emailTemplates.deleteError')}</>);
    } finally {
      if (isMounted()) setIsDeleting(false);
    }
  };

  const onRestore = async (template: GroupMessageEmailTemplate) => {
    try {
      await restoreGroupMessageEmailTemplate(template.id);
      if (isMounted()) {
        toast.success(<>{t('groupMessage:emailTemplates.restoreSuccess')}</>);
        fetchDeleted();
      }
    } catch (e) {
      if (isMounted()) toast.error(<>{t('groupMessage:emailTemplates.restoreError')}</>);
    }
  };

  const activeColumns: GridColDef[] = [
    { field: 'name', headerName: t('groupMessage:emailTemplates.tableHeaders.name'), flex: 2 },
    {
      field: 'createdBy',
      headerName: t('groupMessage:emailTemplates.tableHeaders.createdBy'),
      flex: 2,
      valueGetter: (params) => (params.row.localAss_id > 0 ? params.row.localAssLabel : params.row.createdByLabel || params.row.countyAssLabel || ''),
    },
    {
      field: 'createdAt',
      headerName: t('groupMessage:emailTemplates.tableHeaders.createdAt'),
      flex: 1,
      valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleDateString('sv-SE') : ''),
    },
    {
      field: 'actions',
      headerName: '',
      width: canDistribute ? 140 : 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GroupMessageEmailTemplate>) => (
        <>
          {canDistribute && (
            <Tooltip title={t('groupMessage:emailTemplates.distributeTitle')}>
              <IconButton size='small' onClick={() => setDistributeTemplate(params.row)}>
                <ContentCopy fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t('common:button.edit')}>
            <IconButton size='small' onClick={() => openEdit(params.row)}>
              <Edit fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common:button.delete')}>
            <IconButton size='small' onClick={() => setDeleteTemplate(params.row)}>
              <Delete fontSize='small' />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const deletedColumns: GridColDef[] = [
    { field: 'name', headerName: t('groupMessage:emailTemplates.tableHeaders.name'), flex: 2 },
    {
      field: 'createdBy',
      headerName: t('groupMessage:emailTemplates.tableHeaders.createdBy'),
      flex: 2,
      valueGetter: (params) => (params.row.localAss_id > 0 ? params.row.localAssLabel : params.row.createdByLabel || params.row.countyAssLabel || ''),
    },
    {
      field: 'deletedAt',
      headerName: t('groupMessage:emailTemplates.tableHeaders.deletedAt'),
      flex: 1,
      valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleDateString('sv-SE') : ''),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GroupMessageEmailTemplate>) => (
        <Tooltip title={t('groupMessage:archiveGroupMessage.undoArchive')}>
          <IconButton size='small' onClick={() => onRestore(params.row)}>
            <RestoreFromTrash fontSize='small' />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const gridProps = {
    disablePointer: true,
    autoHeight: true,
    checkboxSelection: false,
    selectionModel: [] as [],
    mode: 'client' as const,
    onPageChange: () => undefined,
    onPageSizeChange: () => undefined,
    onSelectionModelChange: () => undefined,
  };

  return (
    <>
      {canDistribute && (
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showOnlyFederation}
                onChange={(e) => {
                  setShowOnlyFederation(e.target.checked);
                  setDistrict(undefined);
                  setAssociation(undefined);
                }}
                size='small'
              />
            }
            label={t('groupMessage:emailTemplates.showOnlyFederation')}
          />
          <Box sx={{ display: showOnlyFederation ? 'none' : 'flex', alignItems: 'center', ml: 'auto' }}>
            <AssociationAndDistrictSelect
              hideDistrict={!currentUser?.districts?.length}
              district={district}
              association={association}
              districtCallback={(val) => {
                setDistrict(val || undefined);
                setAssociation(undefined);
              }}
              associationCallback={(val) => setAssociation(val || undefined)}
            />
          </Box>
        </Box>
      )}

      <Tabs value={activeTab} textColor='primary' onChange={(_, v) => setActiveTab(v)} sx={{ '& button': { textTransform: 'none' } }}>
        <Tab label={t('groupMessage:emailTemplates.tabs.active')} />
        <Tab
          sx={{ marginLeft: 'auto', minHeight: 'auto' }}
          label={t('groupMessage:emailTemplates.tabs.deleted')}
          icon={<Delete sx={{ mb: '2px' }} fontSize='small' />}
          iconPosition='start'
        />
      </Tabs>
      <Divider />

      {activeTab === 0 && (
        <DataGrid
          {...gridProps}
          rows={visibleTemplates}
          columns={activeColumns}
          getRowId={(row) => row.id}
          dataModel={{ loading: isLoading, totalRows: visibleTemplates.length, rowsPerPageOptions: [100], pageSize: 100, page: 0 }}
        />
      )}

      {activeTab === 1 && (
        <DataGrid
          {...gridProps}
          rows={visibleDeletedTemplates}
          columns={deletedColumns}
          getRowId={(row) => row.id}
          dataModel={{ loading: isLoading, totalRows: visibleDeletedTemplates.length, rowsPerPageOptions: [100], pageSize: 100, page: 0 }}
        />
      )}

      {editTemplate && (
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSave)}>
            <GenericDialog
              open
              maxWidth='lg'
              dialogTitle={t('groupMessage:emailTemplates.editTitle')}
              dialogSubTitle={editTemplate.localAssLabel || editTemplate.countyAssLabel}
              onClose={() => setEditTemplate(null)}
              onSave={() => ConsoleHelper.log()}
              dialogContent={
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <ControlledTextField label={t('groupMessage:create.templateName')} name='name' />
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                        borderBottom: 1,
                        borderColor: 'divider',
                        px: 1,
                      }}
                    >
                      <Tabs
                        value={effectivePreviewTab}
                        onChange={(_, value: 'preview' | 'edit') => setEditPreviewTab(value)}
                        sx={{ minHeight: 'auto' }}
                      >
                        {!designModeAvailable && (
                          <Tab
                            value='edit'
                            label={t('groupMessage:sections.editContent')}
                            icon={<Edit fontSize='small' />}
                            iconPosition='start'
                            sx={{ minHeight: 36, py: 0 }}
                          />
                        )}
                        <Tab
                          value='preview'
                          label={t('groupMessage:builder.previewTab')}
                          icon={<Pageview fontSize='small' />}
                          iconPosition='start'
                          sx={{ minHeight: 36, py: 0 }}
                        />
                      </Tabs>
                      <Tooltip title={!designModeAvailable ? (t('groupMessage:builder.designModeLockedTooltip') as string) : ''}>
                        <span>
                          <Button
                            size='small'
                            variant='outlined'
                            startIcon={<DashboardCustomize fontSize='small' />}
                            onClick={() => setBuilderMode('design')}
                            disabled={!designModeAvailable}
                          >
                            {t('groupMessage:builder.designMode')}
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>

                    {effectivePreviewTab === 'preview' && (
                      <Box sx={{ pt: 2 }}>
                        <GroupMessageEmailTemplateView body={editBody || ''} height='40rem' />
                      </Box>
                    )}
                    {effectivePreviewTab === 'edit' && (
                      <Box sx={{ pt: 2 }}>
                        <ControlledTextEditor name='body' noMargin rows={32} />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              }
              dialogActions={
                <>
                  <Button variant='outlined' disableElevation onClick={() => setEditTemplate(null)}>
                    {t('common:button.cancel')}
                  </Button>
                  <Button variant='contained' disableElevation disabled={isSaving} onClick={handleSubmit(onSave)}>
                    {t('common:button.save')}
                  </Button>
                </>
              }
            />
            <EmailBuilderModal open={builderMode !== null} mode={builderMode ?? 'direct'} onClose={() => setBuilderMode(null)} />
          </form>
        </FormProvider>
      )}

      {distributeTemplate && <EmailTemplateDistributeModal template={distributeTemplate} onClose={() => setDistributeTemplate(null)} />}

      {deleteTemplate && (
        <GenericDialog
          open
          maxWidth='sm'
          dialogTitle={t('groupMessage:emailTemplates.deleteConfirmTitle')}
          onClose={() => setDeleteTemplate(null)}
          onSave={() => ConsoleHelper.log()}
          dialogContent={<>{t('groupMessage:emailTemplates.deleteConfirmText', { name: deleteTemplate.name })}</>}
          dialogActions={
            <>
              <Button variant='outlined' disableElevation onClick={() => setDeleteTemplate(null)}>
                {t('common:button.cancel')}
              </Button>
              <Button variant='contained' color='error' disableElevation disabled={isDeleting} onClick={onDelete}>
                {t('common:button.delete')}
              </Button>
            </>
          }
        />
      )}
    </>
  );
};
